import React from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { PotStatus } from '../types/pot.types';
import { getStatusColor, getStatusLabel } from './PotStatusBadge';

export interface StatusGroupHeaderProps {
  status: PotStatus;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  'data-testid'?: string;
  panelId?: string;
}

export const StatusGroupHeader = React.memo(function StatusGroupHeader({
  status,
  count,
  expanded,
  onToggle,
  'data-testid': dataTestId,
  panelId,
}: StatusGroupHeaderProps) {
  const theme = useTheme();
  const chipColor = getStatusColor(status);
  const dotColor =
    chipColor === 'default'
      ? theme.palette.grey[500]
      : theme.palette[chipColor].main;

  return (
    <ButtonBase
      onClick={onToggle}
      data-testid={dataTestId}
      aria-expanded={expanded}
      aria-controls={panelId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        px: { xs: 2, sm: 3 },
        py: 1,
        justifyContent: 'flex-start',
        bgcolor: theme =>
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
        '&:hover': {
          bgcolor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.06)'
              : 'rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <ChevronRightIcon
        fontSize="small"
        sx={{
          color: 'text.secondary',
          transition: 'transform 150ms ease',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
        }}
      />
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: dotColor,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" fontWeight={600}>
        {getStatusLabel(status)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {count}
      </Typography>
    </ButtonBase>
  );
});
